import { useMemo } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, Plus, Trash } from 'lucide-react';
import Section from '@/components/Section';
import { Button } from '@/components/ui/button';
import useTeamMembers from '@/hooks/useTeamMembers';
import { ITeamMember } from '@/types';
import instance from '@/utils/axiosClient';
import { formatPhoneNumber } from '@/utils/helpers';
import DataTable from '../DataTable/DataTable';
import Time from '../Time';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';

const Team = () => {
  const teamMembers = useTeamMembers();
  const teamMemberModal = useModal('team-member-drawer');

  const archiveTeamMember = async (id: string) => {
    try {
      const response = await instance.patch(`/v1/account_users/${id}/archive`);
      if (response.status === 200) {
        teamMembers.refetch();
      } else {
        console.error('Failed to archive user:', response.data.errors || response.data.error);
      }
    } catch (error: any) {
      console.error('Failed to archive user:', error.response?.data || error.message);
    }
  };

  const data = useMemo(() => teamMembers.data || [], [teamMembers.data]);

  const columns: ColumnDef<ITeamMember>[] = useMemo(
    () => [
      {
        accessorKey: 'user.first_name',
        header: 'First Name',
      },
      {
        accessorKey: 'user.last_name',
        header: 'Last Name',
      },
      {
        accessorKey: 'user.phone',
        header: 'Phone',
        cell: ({ row }) => formatPhoneNumber(row.original.user.phone),
      },
      {
        accessorKey: 'user.email',
        header: 'Email',
      },
      {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => <Badge>{row.original.role}</Badge>,
      },
      {
        accessorKey: 'created_at',
        header: 'Invited',
        cell: ({ row }) => <Time>{row.original.created_at}</Time>,
      },
      {
        header: 'Actions',
        cell: ({ row }) => {
          const teamMember = row.original;

          return (
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  teamMemberModal.show({ accountUser: teamMember });
                }}
              >
                <Edit className="mr-1" />
                Edit
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash className="mr-1" />
                    Archive
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive Team Member</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to archive {teamMember.user.first_name} {teamMember.user.last_name}? This
                      action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => archiveTeamMember(teamMember.id)}>Archive</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        },
      },
    ],
    [teamMemberModal, archiveTeamMember],
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-8">
        <h2 className="mb-4 text-lg font-bold">Team Members</h2>
        <Button className="ml-auto" onClick={() => teamMemberModal.show()}>
          <Plus className="mr-1" />
          Add Team Member
        </Button>
      </div>

      <Section>
        <DataTable data={data} columns={columns} model={teamMembers} />
      </Section>
    </div>
  );
};

export default Team;
